/* ═══════════════════════════════════════════════════════════════════════
   KI-PDP — product page behaviour, Kraft & Ink (2026-10-03).
   Loaded (defer) by sections/main-product.liquid. Additive to theme.js:
   theme.js still owns form[data-product-form] (variant input, ADD price,
   AJAX add + drawer). Everything here degrades: no JS = native form post,
   a swipeable gallery, native <details>, a native customer-form post.

   No default formula (2026-10-08, snippets/pdp-buy-box.liquid): the
   formula switch is two links. Unless ?formula= names THIS product's
   formula, the page opens unchosen: ADD gets aria-disabled="true" and
   reads the prompt (focusable, never `disabled`), the sticky bar reads
   "Choose formula", and both only point at the switch. Tapping this
   product's card chooses it in place; the twin's card navigates (with
   ?formula= and the chosen pack as ?pack=). Without JS the links and the
   form work as plain HTML.

   Listens:  click on [data-kp-ladder] [data-variant-id], gallery scroll,
             shopify:section:load (re-init), document 'cc:gate:closed'
             (snippets/age-gate.liquid closeGate, every close path),
             click [data-gate-enter] (fallback).
   Emits:    document CustomEvent 'cc:analytics'
               { name: 'pdp_pack_select', props: { handle, pack } }
               { name: 'pdp_notify_submit', props: { handle, pack } }
               { name: 'pdp_film_play', props: { handle } }
               { name: 'pdp_formula_choose', props: { handle, formula } }
               { name: 'pdp_formula_prompt', props: { handle } }
   ═══════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var reduceMq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  function reduced() { return !!(reduceMq && reduceMq.matches); }
  function track(name, props) {
    try { document.dispatchEvent(new CustomEvent('cc:analytics', { detail: { name: name, props: props || {} } })); } catch (e) {}
  }
  function announce(text) {
    var live = document.querySelector('[data-cc-live]');
    if (!live) return;
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 60);
  }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ── Gallery: counter, prev/next, loop video only while visible ── */
  function initGallery(root) {
    var track = root.querySelector('[data-kp-track]');
    if (!track) return null;
    var slides = qsa('[data-slide]', track);
    var idxEl = root.querySelector('[data-kp-index]');
    var totalEl = root.querySelector('[data-kp-total]');
    var prev = root.querySelector('[data-kp-prev]');
    var next = root.querySelector('[data-kp-next]');
    var current = 0;
    if (totalEl) totalEl.textContent = String(slides.length);
    slides.forEach(function (s, i) {
      s.setAttribute('role', 'group');
      s.setAttribute('aria-roledescription', 'slide');
      s.setAttribute('aria-label', (i + 1) + ' of ' + slides.length);
    });

    function goTo(i) {
      if (!slides[i]) return;
      track.scrollTo({ left: slides[i].offsetLeft - track.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' });
    }
    function sync() {
      var x = track.scrollLeft, best = Infinity, idx = 0;
      slides.forEach(function (s, i) {
        var d = Math.abs(s.offsetLeft - track.offsetLeft - x);
        if (d < best) { best = d; idx = i; }
      });
      current = idx;
      if (idxEl) idxEl.textContent = String(idx + 1);
      if (prev) prev.disabled = idx === 0;
      if (next) next.disabled = idx === slides.length - 1;
      loops.forEach(function (l) { l.update(); });
    }
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; sync(); });
    }, { passive: true });
    if (prev) prev.addEventListener('click', function () { goTo(Math.max(0, current - 1)); });
    if (next) next.addEventListener('click', function () { goTo(Math.min(slides.length - 1, current + 1)); });
    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(Math.min(slides.length - 1, current + 1)); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(Math.max(0, current - 1)); }
    });

    /* Loop videos: muted, no autoplay attribute; play only when the slide
       is the active one AND >= 40% on screen AND not paused by the user AND
       motion is allowed. Reduced motion = poster only (tap pause/play to run). */
    var loops = qsa('[data-kp-loop]', track).map(function (fig) {
      var video = fig.querySelector('video');
      var btn = fig.querySelector('[data-kp-pause]');
      var state = { visible: false, userPaused: reduced(), userPlayed: false };
      function setBtn(playing) {
        if (!btn) return;
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        var ip = btn.querySelector('[data-icon-pause]');
        var pl = btn.querySelector('[data-icon-play]');
        if (ip) ip.hidden = !playing;
        if (pl) pl.hidden = playing;
      }
      function update() {
        var active = slides.indexOf(fig) === current;
        var want = active && state.visible && !state.userPaused && (!reduced() || state.userPlayed);
        if (want && video.paused) {
          var p = video.play();
          if (p && p.catch) p.catch(function () { setBtn(false); });
          setBtn(true);
        } else if (!want && !video.paused) {
          video.pause();
          setBtn(false);
        } else {
          setBtn(!video.paused);
        }
      }
      if (btn) btn.addEventListener('click', function () {
        if (video.paused) { state.userPaused = false; state.userPlayed = true; }
        else { state.userPaused = true; }
        update();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          state.visible = entries[entries.length - 1].intersectionRatio >= 0.4;
          update();
        }, { threshold: [0, 0.4, 1] }).observe(fig);
      } else {
        state.visible = true;
      }
      setBtn(false);
      return { update: update };
    });

    sync();
    return {
      goToKey: function (key) {
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute('data-slide-key') === key) { goTo(i); return; }
        }
      }
    };
  }

  /* ── Pack ladder: pressed state, prices, pack name, notify tags ── */
  function initLadder(root, gallery) {
    var ladder = root.querySelector('[data-kp-ladder]');
    if (!ladder) return;
    var handle = root.getAttribute('data-handle') || '';
    var box = ladder.closest('.kp-form') || root;
    ladder.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-variant-id]');
      if (!btn || btn.disabled) return;
      qsa('[data-variant-id]', ladder).forEach(function (b) { b.setAttribute('aria-pressed', String(b === btn)); });
      var price = btn.getAttribute('data-price-fmt') || '';
      var pack = btn.getAttribute('data-pack') || '';
      var input = box.querySelector('[data-variant-input]');
      if (input) input.value = btn.getAttribute('data-variant-id');
      /* a ?variant= link can open on a sold-out rung (ADD rendered disabled);
         picking an available rung must re-arm ADD and the sticky bar */
      var atc = box.querySelector('[data-kp-atc]');
      if (atc && atc.querySelector('[data-price]')) atc.disabled = false;
      var stickyAdd = document.querySelector('[data-kp-sticky-add]');
      if (stickyAdd) stickyAdd.disabled = false;
      qsa('[data-price]', box).forEach(function (el) { if (price) el.textContent = price; });
      qsa('[data-kp-pack]', box).forEach(function (el) { el.textContent = pack; });
      var sp = document.querySelector('[data-kp-sticky-price]');
      var sk = document.querySelector('[data-kp-sticky-pack]');
      if (sp && price) sp.textContent = price;
      if (sk) sk.textContent = pack;
      var tags = box.querySelector('[data-kp-notify-tags]');
      var cans = btn.getAttribute('data-cans');
      if (tags) tags.value = tags.getAttribute('data-base') + (cans && cans !== '0' ? ',pack-' + cans : '');
      var key = btn.getAttribute('data-gallery-key');
      if (key && gallery) gallery.goToKey(key);
      track('pdp_pack_select', { handle: handle, pack: pack });
    });
  }

  /* ── Formula switch: no default (CLAUDE.md §Compliance) ── */
  function normFormula(f) {
    f = String(f || '').toLowerCase();
    if (!f) return '';
    if (f.indexOf('cbd') > -1 || f.indexOf('micro') > -1) return 'cbd-forward';
    if (f === 'infused') return 'infused';
    return '';
  }
  function param(name) {
    try { return new URLSearchParams(window.location.search).get(name) || ''; } catch (e) { return ''; }
  }
  function initFormula(root) {
    var wrap = root.querySelector('[data-kp-formula-wrap]');
    if (!wrap) return;                       /* no twin: nothing to choose */
    var here = wrap.getAttribute('data-kp-this') || '';
    var handle = root.getAttribute('data-handle') || '';
    var cards = qsa('[data-kp-formula]', wrap);
    var choicesEl = wrap.querySelector('[data-kp-choices]');
    var hint = wrap.querySelector('[data-kp-formula-hint]');
    var form = root.querySelector('form[data-kp-form]');
    var atc = root.querySelector('[data-kp-atc]');
    var ready = atc && atc.querySelector('[data-kp-atc-ready]');
    var prompt = atc && atc.querySelector('[data-kp-atc-prompt]');
    var sticky = document.querySelector('[data-kp-sticky-add]');
    var sReady = sticky && sticky.querySelector('[data-kp-sticky-ready]');
    var sPrompt = sticky && sticky.querySelector('[data-kp-sticky-prompt]');
    var chosen = false;

    function render() {
      root.classList.toggle('is-unchosen', !chosen);
      cards.forEach(function (c) {
        var on = chosen && c.hasAttribute('data-kp-formula-here');
        c.classList.toggle('is-selected', on);
        if (on) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current');
      });
      if (atc && prompt && ready) {
        if (chosen) atc.removeAttribute('aria-disabled'); else atc.setAttribute('aria-disabled', 'true');
        ready.hidden = !chosen;
        prompt.hidden = chosen;
      }
      if (sReady && sPrompt) { sReady.hidden = !chosen; sPrompt.hidden = chosen; }
      if (chosen) {
        if (choicesEl) choicesEl.classList.remove('is-prompting');
        if (hint) hint.hidden = true;
      }
    }
    function choose(user) {
      chosen = true;
      render();
      if (user) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.set('formula', here);
          window.history.replaceState(window.history.state, '', u.toString());
        } catch (e) {}
        var line = wrap.querySelector('[data-kp-formula-here] .ki-dose__line');
        announce((line ? line.textContent : 'Formula') + ' chosen.');
        track('pdp_formula_choose', { handle: handle, formula: here });
      }
    }
    function point() {
      if (choicesEl) choicesEl.classList.add('is-prompting');
      if (hint) { hint.hidden = false; announce(hint.textContent); }
      try { wrap.scrollIntoView({ block: 'center', behavior: reduced() ? 'auto' : 'smooth' }); } catch (e) { wrap.scrollIntoView(); }
      var first = cards[0];
      if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
      track('pdp_formula_prompt', { handle: handle });
    }
    root.__kpFormula = { isChosen: function () { return chosen; }, point: point };

    cards.forEach(function (c) {
      c.addEventListener('click', function (e) {
        if (c.hasAttribute('data-kp-formula-here')) {
          e.preventDefault();
          choose(true);
          return;
        }
        /* the twin: carry the pack the shopper picked */
        var pressed = root.querySelector('[data-kp-ladder] [aria-pressed="true"]');
        var cans = pressed && pressed.getAttribute('data-cans');
        if (cans && cans !== '0') {
          try {
            var u = new URL(c.href, window.location.href);
            u.searchParams.set('pack', cans);
            c.href = u.toString();
          } catch (err) {}
        }
        /* owner 2026-10-08: change channels with a burst of static */
        if (!e.defaultPrevented && !(e.metaKey || e.ctrlKey || e.shiftKey) && pdcTune(c)) {
          e.preventDefault();
          var go = c.href;
          setTimeout(function () { window.location.href = go; }, 320);
        }
      });
    });
    if (atc) {
      atc.addEventListener('click', function (e) {
        if (chosen || !prompt) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        point();
      });
    }
    if (form) {
      /* belt and braces: no submit (Enter key, requestSubmit) while unchosen.
         Capture listeners at the target run before theme.js's own. */
      form.addEventListener('submit', function (e) {
        if (chosen || !prompt) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        point();
      }, true);
    }

    /* arrival: ?formula= naming this product's formula = the shopper's own pick */
    if (normFormula(param('formula')) === here) chosen = true;
    render();

    /* arrival from the twin's card: ?pack=N selects that pack (unless ?variant= pins one) */
    var pack = param('pack');
    if (pack && !param('variant')) {
      var rung = root.querySelector('[data-kp-ladder] [data-cans="' + pack.replace(/[^0-9]/g, '') + '"]');
      if (rung && !rung.disabled && rung.getAttribute('aria-pressed') !== 'true') rung.click();
    }
  }

  /* ── Notify me when ordering opens (store_open off) ── */
  function initNotify(root) {
    qsa('form[data-kp-notify]', root).forEach(function (form) {
      if (form.dataset.kpBound) return;
      form.dataset.kpBound = '1';
      form.addEventListener('submit', function (e) {
        if (!window.fetch) return;
        e.preventDefault();
        var btn = form.querySelector('[data-kp-notify-submit]');
        var note = form.querySelector('[data-kp-notify-note]');
        if (btn) btn.disabled = true;
        fetch(form.action, { method: 'POST', credentials: 'same-origin', body: new FormData(form) })
          .then(function (r) {
            if (!r.ok) throw new Error('post failed');
            /* Shopify's bot protection answers an AJAX /contact post with a
               redirect to /challenge (200) — that is NOT a signup. Fall back
               to the native post so the shopper sees the challenge. */
            if (r.redirected && /\/challenge/.test(r.url || '')) throw new Error('challenge');
            qsa('input, label.ki-check, button', form).forEach(function (el) {
              if (el.type !== 'hidden') el.hidden = true;
            });
            if (note) note.textContent = note.getAttribute('data-success') || 'You’re on the list.';
            announce(note ? note.textContent : 'You’re on the list.');
            var tags = form.querySelector('[data-kp-notify-tags]');
            track('pdp_notify_submit', { handle: root.getAttribute('data-handle') || '', pack: tags ? tags.value : '' });
          })
          .catch(function () {
            if (btn) btn.disabled = false;
            form.submit();   /* native post — still works */
          });
      });
    });
  }

  /* ── Sticky phone bar ──
     Gauntlet 2026-10-23 (PKG-05, site-10, integration fix FX-A): the bar
     shows only once the visitor has scrolled into the buy box (the box's
     top edge has passed above the viewport), and then only while neither
     the panel's own ADD (or notify box) nor the formula chooser is on
     screen: never two buy controls, and the bar never sits over the
     chooser. So the first screen looks as it did before the gauntlet: no
     bar, and the H1 is never under it (a 390x664 iPhone first screen puts
     the H1 inside the bar's strip). "On screen" = above the bar's own strip
     at the bottom of the viewport (rootMargin = the bar's height), since
     anything inside that strip is under the bar. Hidden while the age gate
     is open. Without IntersectionObserver the same test runs from
     getBoundingClientRect on every scroll frame. */
  function initSticky(root) {
    var bar = document.querySelector('[data-kp-sticky]');
    if (!bar || bar.dataset.kpBound) return;
    bar.dataset.kpBound = '1';
    var target = root.querySelector('[data-kp-atc]') || root.querySelector('[data-kp-notify-box]');
    if (!target) return;
    var form = root.querySelector('form[data-product-form]');
    var add = bar.querySelector('[data-kp-sticky-add]');
    var atc = root.querySelector('[data-kp-atc]');
    if (add && form && atc) {
      add.addEventListener('click', function () {
        if (root.__kpFormula && !root.__kpFormula.isChosen()) { root.__kpFormula.point(); return; }
        if (typeof form.requestSubmit === 'function') form.requestSubmit(atc);
        else atc.click();
      });
    }
    var chooser = root.querySelector('[data-kp-formula-wrap]');
    var box = root.querySelector('[data-kp-box]') || target;
    var cover = bar.offsetHeight || 0;     /* 0 from 990px up: the bar is display:none there */
    var targetVisible = true;              /* hidden until the first measurement: no flash */
    var chooserVisible = !!chooser;
    var gate = document.getElementById('gate');
    var io = null;
    function onScreen(el) {
      var r = el.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      return r.bottom > 0 && r.top < vh - cover;
    }
    function apply() {
      var gateOpen = gate && !gate.hidden;
      if (!io) {
        targetVisible = onScreen(target);
        chooserVisible = !!chooser && onScreen(chooser);
      }
      var scrolledIn = box.getBoundingClientRect().top < 0;
      bar.classList.toggle('is-on', scrolledIn && !targetVisible && !chooserVisible && !gateOpen);
    }
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.target === target) targetVisible = en.isIntersecting;
          else if (en.target === chooser) chooserVisible = en.isIntersecting;
        });
        apply();
      }, { threshold: 0, rootMargin: '0px 0px -' + cover + 'px 0px' });
      io.observe(target);
      if (chooser) io.observe(chooser);
    }
    var tick = false;
    window.addEventListener('scroll', function () {
      if (tick) return;
      tick = true;
      requestAnimationFrame(function () { tick = false; apply(); });
    }, { passive: true });
    /* The gate's own handler stops the [data-gate-enter] click and, with
       the state step on, closes from Continue or Skip: cc:gate:closed is
       the one signal every close path fires (gate.hidden is already true). */
    document.addEventListener('cc:gate:closed', apply);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('[data-gate-enter]')) setTimeout(apply, 60);
    });
    apply();
  }

  /* ── Tini film: tap to play (never autoplays) ── */
  function initFilm(root) {
    qsa('[data-kp-film]', root).forEach(function (frame) {
      var video = frame.querySelector('video');
      var btn = frame.querySelector('[data-kp-film-play]');
      if (!video || !btn || btn.dataset.kpBound) return;
      btn.dataset.kpBound = '1';
      btn.addEventListener('click', function () {
        video.controls = true;
        btn.hidden = true;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
        video.focus();
        track('pdp_film_play', { handle: root.getAttribute('data-handle') || '' });
      });
    });
  }

  function init(scope) {
    qsa('[data-kp-root]', scope).forEach(function (root) {
      if (root.dataset.kpInit) return;
      root.dataset.kpInit = '1';
      var gallery = initGallery(root);
      initLadder(root, gallery);
      initFormula(root);
      initNotify(root);
      initSticky(root);
      initFilm(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
})();

/* -- TV static between formula pages (owner 2026-10-08) -- */
function pdcStill() { return window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches; }
function pdcOverlay(label) {
  var o = document.createElement('div');
  o.className = 'pdc-tune'; o.setAttribute('aria-hidden', 'true');
  o.innerHTML = '<span class="pdc-tune__snow"></span><span class="pdc-tune__bar"></span><span class="pdc-tune__osd"></span>';
  o.querySelector('.pdc-tune__osd').textContent = label || '';
  document.body.appendChild(o);
  return o;
}
function pdcTune(link) {
  if (pdcStill()) return false;
  var line = link.querySelector('.ki-dose__line');
  var label = 'CH • ' + (line ? line.textContent.trim() : 'Changing formula');
  try { sessionStorage.setItem('cc-pdc-tune', label); } catch (e) {}
  pdcOverlay(label);
  return true;
}
(function () {
  var label = null;
  try { label = sessionStorage.getItem('cc-pdc-tune'); sessionStorage.removeItem('cc-pdc-tune'); } catch (e) {}
  if (!label || pdcStill()) return;
  function run() {
    var o = pdcOverlay(label);
    setTimeout(function () { o.classList.add('is-out'); setTimeout(function () { if (o.parentNode) o.parentNode.removeChild(o); }, 520); }, 260);
  }
  if (document.body) run(); else document.addEventListener('DOMContentLoaded', run);
})();
